import { User } from "@iventis/domain-model/model/user";
import { UserGroupWithUsers } from "@iventis/domain-model/model/userGroupWithUsers";
import { UserGroup } from "@iventis/domain-model/model/userGroup";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import {
    PostNewUserAndGroups,
    RemovePeopleApi,
    ResetPasswordsApi,
    GetTeams,
    RemovePersonFromTeamApi,
    PostTeam,
    DeleteTeam,
    PutTeamLeader,
    AddPeopleToTeamApi,
    GetPeople,
    EnableSSoApi,
    DisableSSoApi,
} from "@iventis/people";
import { permissionsApi } from "@iventis/api/src/api";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import { toast } from "@iventis/toasts/src/toast";

export const removePeople: RemovePeopleApi = (callback) => (userIds, projectId) =>
    permissionsApi
        // transformResponse is necessary here, otherwise the then clause will not fire
        .delete<void, { userIds: string[] }>(`project/${projectId}/users`, {
            data: userIds,
            transformResponse: () => userIds,
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.people.remove_people.remove_user)?.toLocaleLowerCase?.() }),
                message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.people.remove_people.remove_user) }),
            },
        })
        .then((data) => {
            callback?.(userIds);
            return data;
        });

export const resetPasswords: ResetPasswordsApi = (userIds) =>
    permissionsApi.post<void, { userIds: string[] }>("authentication/local_password/tokens", userIds, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.people.reset_password.reset_password_title)?.toLocaleLowerCase?.() }),
            message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.people.reset_password.reset_password_title) }),
        },
    });

export const enableSso: EnableSSoApi = async (userIds, projectId: string) => {
    try {
        await Promise.all(userIds.map((id) => permissionsApi.patch(`project/${projectId}/users/${id}`, { ssoEnabled: true }, { suppressErrors: true })));
    } catch (err) {
        toast.error({
            title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.people.sso.title)?.toLocaleLowerCase?.() }),
            message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.people.sso.title) }),
        });
    }
    return { userIds };
};

export const disableSso: DisableSSoApi = async (userIds, projectId: string) => {
    await Promise.all(userIds.map((id) => permissionsApi.patch(`project/${projectId}/users/${id}`, { ssoEnabled: false })));
    return { userIds };
};

export const postNewUserAndGroups: PostNewUserAndGroups = (callback) => (userAndGroupsRequest) =>
    permissionsApi
        .post<User[]>("/users", userAndGroupsRequest, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.people.add_people.add_user)?.toLocaleLowerCase?.() }),
                message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.people.add_people.add_user) }),
            },
        })
        .then(({ data }) => {
            callback?.(data);
            return data;
        });

export const getTeams: GetTeams = () => permissionsApi.get<UserGroupWithUsers[]>("/groups").then(({ data }) => data);

export const removePersonFromTeam: RemovePersonFromTeamApi = (teamId: string, personId: string) =>
    permissionsApi.delete(`groups/${teamId}/users/${personId}`, {
        customErrorMessage: {
            title: translate(Content.people.toasts.removeFromTeamError),
            message: translate(Content.people.toasts.removeFromTeamError),
        },
    });

export const createTeam: PostTeam = (name: string) =>
    permissionsApi
        .post<UserGroup>(`/groups/${name}`, undefined, {
            customErrorMessage: {
                title: translate(Content.people.toasts.add_team_error),
                message: translate(Content.people.toasts.add_team_error),
            },
        })
        .then(({ data }) => data);

export const deleteTeam: DeleteTeam = (id: string) =>
    permissionsApi
        .delete(`/groups/${id}`, {
            customErrorMessage: {
                title: translate(Content.people.toasts.deleteTeamError),
                message: translate(Content.people.toasts.deleteTeamError),
            },
        })
        .then(() => id);

export const getPeople: GetPeople = (projectId: string) => permissionsApi.get<User[]>(`project/${projectId}/users`).then(({ data }) => data);

export const updateTeamLeader: PutTeamLeader = (teamId: string, userId: string, authorisation: Authorisation) =>
    permissionsApi.put(`/groups/${teamId}/user/${userId}`, JSON.stringify(authorisation), {
        headers: {
            "Content-Type": "application/json",
        },
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.people.teamLeader) }),
            message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.people.teamLeader) }),
        },
    });

export const addPeopleToTeam: AddPeopleToTeamApi = (teamId: string, personIds: string[]) => {
    if (personIds.length === 0) {
        return Promise.resolve();
    }
    return permissionsApi.post(`/groups/${teamId}/users`, personIds, {
        customErrorMessage: {
            title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.people.addTeamMember)?.toLocaleLowerCase?.() }),
            message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.people.addTeamMember) }),
        },
    });
};
